import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format, startOfYear, endOfYear } from "date-fns";
import { LuCreditCard, LuPrinter } from "react-icons/lu";
import { GenericTable, TableColumn } from "../ui/table";
import useFetchUnpaidBookings from "./hooks/useFetchUnpaidBookings";
import useManagementPayBookings from "./hooks/useManagementPayBookings";
import { UnpaidBookingItem } from "../../api/types";
import useShowToast from "../../hooks/useShowToast";
import api from "../../api";
import { formatCurrency } from "../../utils/numbers";

const ClubManagementUnpaidHours = (): ReactElement => {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState<Date>(startOfYear(new Date()));
  const [dateTo, setDateTo] = useState<Date>(endOfYear(new Date()));
  const [username, setUsername] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<UnpaidBookingItem[]>([]);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const { unpaidBookings, loading, refetchUnpaidBookings } = useFetchUnpaidBookings();

  const { payBookings, isLoading: isPaying } = useManagementPayBookings();
  const { errorToast } = useShowToast();

  useEffect(() => {
    refetchUnpaidBookings(dateFrom, dateTo, username || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    const now = new Date();
    setDateFrom(startOfYear(now));
    setDateTo(endOfYear(now));
    setUsername("");
    refetchUnpaidBookings(startOfYear(now), endOfYear(now), null);
  };

  const refetch = () => {
    refetchUnpaidBookings(dateFrom, dateTo, username || null);
  };

  const handleRowSelectionChange = (rows: unknown) => {
    setSelectedRows(rows as UnpaidBookingItem[]);
  };

  const handlePayBookings = async () => {
    const bookingIds = selectedRows.map(row => row.id);
    const success = await payBookings(bookingIds);
    if (success) {
      setSelectedRows([]);
      refetchUnpaidBookings(dateFrom, dateTo, username || null);
    }
  };

  const handlePrint = async () => {
    setIsPrintLoading(true);
    await api.printUnpaidBookings(dateFrom, dateTo, username || null);
    if (api.error) {
      errorToast({
        title: t("club_management.unpaid_hours.print.error_title"),
        description: t("club_management.unpaid_hours.print.error_description"),
      });
    }
    setIsPrintLoading(false);
  };

  const columns: TableColumn[] = useMemo(
    () => [
      {
        label: t("club_management.unpaid_hours.table.date"),
        key: "date",
        sortable: true,
        render: (value: unknown) => format(new Date(value as string), "dd/MM/yyyy"),
      },
      {
        label: t("club_management.unpaid_hours.table.hour"),
        key: "hour_label",
        sortable: true,
      },
      {
        label: t("club_management.unpaid_hours.table.court"),
        key: "court_label",
        sortable: true,
      },
      {
        label: t("club_management.unpaid_hours.table.user"),
        key: "username",
        sortable: true,
      },
      {
        label: t("club_management.unpaid_hours.table.amount"),
        key: "amount",
        sortable: true,
        render: (value: unknown) => formatCurrency(value as number),
      },
    ],
    [t],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="200px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        gap={3}
        width="100%"
      >
        <Heading size="lg">{t("club_management.unpaid_hours.title")}</Heading>
        <Button
          leftIcon={<LuPrinter />}
          colorScheme="blue"
          size="sm"
          onClick={handlePrint}
          isLoading={isPrintLoading}
        >
          {t("club_management.unpaid_hours.actions.print")}
        </Button>
      </Box>

      {/* Filters */}
      <Box p={4} bg="gray.50" borderRadius="md">
        <Box
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          gap={4}
          alignItems={{ base: "stretch", md: "end" }}
        >
          <FormControl>
            <FormLabel>{t("club_management.unpaid_hours.filters.date_from")}</FormLabel>
            <Input
              type="date"
              value={format(dateFrom, "yyyy-MM-dd")}
              onChange={e => setDateFrom(new Date(e.target.value))}
              size="sm"
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("club_management.unpaid_hours.filters.date_to")}</FormLabel>
            <Input
              type="date"
              value={format(dateTo, "yyyy-MM-dd")}
              onChange={e => setDateTo(new Date(e.target.value))}
              size="sm"
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("club_management.unpaid_hours.filters.user")}</FormLabel>
            <Input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder={t("club_management.unpaid_hours.filters.username_placeholder")}
              size="sm"
            />
          </FormControl>
          <Button onClick={refetch} size="sm" pl={8} pr={8} colorScheme="green">
            {t("club_management.unpaid_hours.filters.filter")}
          </Button>
          <Button onClick={resetFilters} size="sm" pl={8} pr={8} colorScheme="blue">
            {t("club_management.unpaid_hours.filters.reset")}
          </Button>
        </Box>
      </Box>

      {/* Actions */}
      {selectedRows.length > 0 && (
        <Box
          p={4}
          bg="gray.50"
          borderRadius="md"
          position="sticky"
          top={0}
          zIndex={10}
          boxShadow="sm"
        >
          <Text mb={3} fontWeight="medium">
            {selectedRows.length} {t("club_management.unpaid_hours.selected_bookings")}
          </Text>
          <HStack spacing={3}>
            <Button
              leftIcon={<LuCreditCard />}
              colorScheme="green"
              size="sm"
              onClick={handlePayBookings}
              isLoading={isPaying}
            >
              {t("club_management.unpaid_hours.actions.pay")}
            </Button>
          </HStack>
        </Box>
      )}

      <GenericTable
        data={unpaidBookings as unknown as Record<string, unknown>[]}
        columns={columns}
        size="sm"
        enableRowSelection
        onRowSelectionChange={handleRowSelectionChange}
      />
    </VStack>
  );
};

export default ClubManagementUnpaidHours;
